import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCmsPage, c } from '../../lib/cms';

const jsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'SoftwareApplication',
      name: 'NDS DataVault',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description:
        'Dental PMS data integration platform for U.S. DSOs. Extract, normalize, and warehouse data from Dentrix, Eaglesoft, Open Dental, CareStack, and Planet DDS into a unified dental data warehouse for analytics and reporting.',
      offers: {
        '@type': 'Offer',
        url: 'https://xentrios.com/demo',
      },
      provider: {
        '@type': 'Organization',
        name: 'National Data Solutions',
        url: 'https://xentrios.com',
      },
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is dental PMS data integration?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: `Dental PMS data integration is the process of extracting data from one or more practice management systems — Dentrix, Eaglesoft, Open Dental, CareStack, Planet DDS, or others — transforming it into a consistent normalized format, and loading it into a centralized data warehouse. For a DSO that has acquired practices running different PMS platforms, integration is the only way to achieve a single view of AR, production, payer performance, and collections across the enterprise. Without integration, each location's data lives in an isolated system, requiring manual exports and spreadsheet consolidation for any group-level report.`,
          },
        },
        {
          '@type': 'Question',
          name: 'How does NDS DataVault connect to Dentrix, Eaglesoft, and Open Dental?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'NDS DataVault uses purpose-built connectors for each major dental PMS — not generic database scrapers. Each connector understands the schema, data model, and update patterns of its target PMS, which means the extraction is reliable and the normalization is accurate. Dentrix uses a SQL Server backend with a specific table structure for claims and appointments; Eaglesoft uses its own relational schema; Open Dental is open-source with a documented schema. NDS maintains connectors for each, handling schema differences, version updates, and incremental sync schedules. For cloud-based PMS platforms like CareStack and Planet DDS, NDS connects via API.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can NDS DataVault handle a DSO that uses multiple different PMS systems?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes — this is the primary use case NDS DataVault was built for. A DSO that has grown through acquisition commonly finds itself with a mix of PMS environments: Dentrix at one group of acquired practices, Open Dental at another, CareStack at a third. NDS DataVault connects to all of them simultaneously, normalizes the data from each into a unified schema, and loads it into a single data warehouse. The result is that InsightFlow, RevTech analytics tools, and Power BI all see a single consistent dataset regardless of how many different PMS systems are in the field.',
          },
        },
        {
          '@type': 'Question',
          name: 'What data from the PMS does NDS DataVault extract?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: `NDS DataVault extracts the full breadth of clinically and financially relevant PMS data: patient demographics and insurance coverage, scheduled and completed procedures with CDT codes, claims submitted and their adjudication status, ERA/EOB payment records, payer contracts and fee schedules, provider schedules and production records, and appointment history. This data is normalized across PMS environments so that a Dentrix "claim" record and an Open Dental "claim" record map to the same fields in the DataVault schema — enabling true cross-location analytics that wouldn't be possible against the raw PMS data.`,
          },
        },
      ],
    },
  ],
};

const connectors = [
  {
    icon: '🦷',
    name: 'Dentrix Integration',
    desc: 'Direct connector to the Dentrix SQL Server database. Incremental sync of claims, patient records, production, and payment data. Compatible with Dentrix G4, G5, G6, G7, and current cloud versions. Handles schema differences across versions automatically.',
  },
  {
    icon: '🦅',
    name: 'Eaglesoft Integration',
    desc: 'Native Eaglesoft connector with full claim and production data extraction. Supports all current Eaglesoft versions. Normalized to the DataVault schema so Eaglesoft data is directly comparable to data from other PMS platforms in your group.',
  },
  {
    icon: '🌐',
    name: 'Open Dental Integration',
    desc: `Open Dental's open-source, documented schema allows for a precise connector with deep field-level mapping. NDS extracts procedure, claim, payment, and patient data and normalizes it for cross-location comparison alongside Dentrix and Eaglesoft environments.`,
  },
  {
    icon: '☁️',
    name: 'CareStack Integration',
    desc: `API-based integration with CareStack's cloud PMS. Real-time and scheduled sync of appointments, procedures, claims, and payments. CareStack data flows into the same DataVault warehouse, enabling consolidated analytics across cloud and on-premise PMS environments.`,
  },
  {
    icon: '🌍',
    name: 'Planet DDS Integration',
    desc: 'API connector for Planet DDS (Denticon, Apteryx, and other Planet DDS products). Extracts clinical and financial data and maps it to the DataVault unified schema — bringing Planet DDS locations into the same enterprise analytics view as all other PMS environments.',
  },
  {
    icon: '🔧',
    name: 'Custom PMS Connectors',
    desc: 'Running a less common PMS, a legacy system, or a proprietary platform? NDS builds custom connectors for any PMS with accessible data — SQL, API, file-based export, or direct database access. Custom connectors follow the same normalization process and integrate into the DataVault warehouse.',
  },
];

const useCases = [
  {
    title: 'Cross-location production reports',
    detail:
      'Consolidated production by provider, location, and CDT code category — without exporting from multiple PMS systems or reconciling different report formats manually.',
  },
  {
    title: 'Consolidated AR across all PMS environments',
    detail:
      'Insurance aging across all locations, all payers, and all PMS platforms in a single enterprise dashboard. Drill from group totals to individual claim detail in one view.',
  },
  {
    title: 'Payer mix analysis',
    detail:
      'Understand your true enterprise payer mix — fee-for-service vs. in-network, by payer and by location. Identify where network participation decisions are affecting revenue.',
  },
  {
    title: 'KPI trending over time',
    detail:
      'Net collection rate, days in AR, denial rate, production per patient visit, case acceptance rate — trended at group, location, and provider level over any date range.',
  },
  {
    title: 'Acquisition due diligence data',
    detail:
      `When evaluating an acquisition, DataVault can extract and normalize historical data from the target practice's PMS to produce standardized financial and production metrics for valuation.`,
  },
  {
    title: 'Regulatory and compliance reporting',
    detail:
      'Consistent, auditable data extracts for compliance purposes. DataVault maintains a complete historical record of all extracted data with timestamped sync logs.',
  },
];

const integrations = ['Dentrix', 'Eaglesoft', 'Open Dental', 'CareStack', 'Planet DDS', 'Xentrios'];

export default function DentalPMSIntegrationPage() {
  const cmsPage = useCmsPage('xentrios', 'dental-pms-integration');
  const heroHeadline = c(cmsPage, 'hero', 'headline', 'Dental PMS Data Integration Platform');
  const heroSubtitle = c(cmsPage, 'hero', 'subtitle', 'RevTech Partners, National Data Solutions, and Xentrios provide an integrated dental operating intelligence stack for DSOs: RCM automation, denial management, reconciliation, analytics, data warehousing, PMS integration, and cloud-native practice management.');
  const connectorItems = c(cmsPage, 'features', 'items', connectors);
  const useCaseItems = c(cmsPage, 'benefits', 'items', useCases);
  const integrationItems = c(cmsPage, 'integrations', 'items', integrations);
  return (
    <div>
      <Helmet>
        <title>Dental PMS Data Integration Platform</title>
        <meta
          name="description"
          content="National Data Solutions (NDS) provides dental PMS data integration for U.S. DSOs: extract, normalize, and warehouse data from Dentrix, Eaglesoft, Open Dental, CareStack, and Planet DDS into a unified dental data warehouse for analytics and reporting."
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="xen-hero">
        <div className="xen-container">
          <p className="xen-hero-eyebrow">Dental PMS Integration</p>
          <h1>{heroHeadline}</h1>
          <p className="subtitle">{heroSubtitle}</p>
          <div className="xen-hero-actions">
            <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.75rem 2rem', fontSize: '1rem' }}>
              Request a Demo
            </Link>
            <Link
              to="/stack"
              className="xen-btn xen-btn-outline"
              style={{ padding: '0.75rem 2rem', fontSize: '1rem', borderColor: '#fff', color: '#fff' }}
            >
              See the Full Stack
            </Link>
          </div>
        </div>
      </section>

      {/* The PMS integration problem */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">The Problem</p>
          <h2>The PMS integration problem for multi-PMS DSOs</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2rem' }}>
            Growth through acquisition is the defining characteristic of the modern DSO model. The data
            problem it creates is the defining challenge.
          </p>
          <div className="xen-two-col" style={{ gap: '3rem' }}>
            <div>
              <p>
                When a dental group acquires a practice, it acquires its PMS along with it. The acquired
                practice has years of clinical and financial data stored in Dentrix, or Eaglesoft, or Open
                Dental — data that can't simply be abandoned. And in most cases, a full PMS migration at
                every acquired location is neither operationally practical nor financially justified in the
                near term.
              </p>
              <p style={{ marginTop: '1rem' }}>
                The result is a DSO operating with a fragmented technology estate. Dentrix at the original
                group of practices. Open Dental at the practices acquired from a smaller group that used it.
                CareStack at the locations that were already on cloud PMS before acquisition. Each system
                holds its own patient records, its own claim history, its own financial data — with no
                native way to see across all of them at once.
              </p>
              <p style={{ marginTop: '1rem' }}>
                The consequences are felt at every level of the organization. The CFO can't see consolidated
                production and collections without waiting for someone to export from three different systems
                and merge the spreadsheets. The RCM director can't compare AR aging across locations running
                different PMS platforms. The data analyst trying to build a Power BI dashboard has three
                completely different schemas to work with, none of which use the same field names.
              </p>
            </div>
            <div>
              <p>
                What's needed is a dedicated data integration layer that sits between your PMS environments
                and your analytics tools — extracting data from each system, normalizing it to a consistent
                schema, and loading it into a central warehouse that the rest of your stack can query.
              </p>
              <p style={{ marginTop: '1rem' }}>
                That's exactly what NDS DataVault does. National Data Solutions maintains purpose-built
                connectors for Dentrix, Eaglesoft, Open Dental, CareStack, and Planet DDS — plus the
                capability to build custom connectors for any PMS. Each connector handles the extraction
                and schema mapping for its target system; the DataVault normalization layer converts
                everything into a unified dental data model; and the resulting warehouse feeds InsightFlow
                analytics, RevTech RCM tools, and any BI platform your team uses.
              </p>
              <p style={{ marginTop: '1rem' }}>
                The end state: a single source of truth for every claim, every payment, every production
                record, and every patient record across your entire portfolio — regardless of how many
                different PMS systems you're running or how many locations you have.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* NDS connector feature grid */}
      <section className="xen-section xen-section-center" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">NDS Connectors</p>
          <h2>Purpose-built connectors for every major dental PMS.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Each NDS connector is built for its specific PMS — not adapted from a generic database
            scraper. That means accurate extraction, reliable incremental sync, and clean data in the warehouse.
          </p>
          <div className="xen-features">
            {connectorItems.map((conn) => (
              <div key={conn.name} className="xen-feature" style={{ textAlign: 'left' }}>
                <div className="xen-feature-icon" style={{ fontSize: '1.75rem' }}>{conn.icon}</div>
                <h3 style={{ color: 'var(--xen-navy)' }}>{conn.name}</h3>
                <p>{conn.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DataVault architecture */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">Architecture</p>
          <h2>How the DataVault pipeline works</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            NDS DataVault is a three-stage pipeline: extraction, normalization, and delivery.
            Each stage is designed for reliability, accuracy, and dental-specific data fidelity.
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '0',
              marginBottom: '2.5rem',
            }}
          >
            {[
              {
                label: 'Extract',
                sub: 'PMS connectors pull claim, patient, production, and payment data on scheduled or real-time sync intervals.',
                color: 'var(--xen-navy)',
              },
              {
                label: 'Transform',
                sub: 'NDS normalization layer maps PMS-specific fields to the unified dental data schema, resolving cross-system inconsistencies.',
                color: 'var(--xen-accent)',
              },
              {
                label: 'Load',
                sub: 'Normalized data is written to the DataVault warehouse — a clean, queryable dataset that all analytics tools use as their source of truth.',
                color: 'var(--xen-teal)',
              },
              {
                label: 'Deliver',
                sub: 'InsightFlow, RevTech RCM tools, Power BI, Tableau, and any other connected system query the warehouse directly — no additional ETL required.',
                color: '#388e3c',
              },
            ].map((step, i, arr) => (
              <React.Fragment key={step.label}>
                <div
                  style={{
                    background: step.color,
                    color: '#fff',
                    borderRadius: '10px',
                    padding: '1.5rem 1.5rem',
                    minWidth: '160px',
                    maxWidth: '220px',
                    textAlign: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    flex: '1 1 160px',
                  }}
                >
                  <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: '0.6rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    {step.label}
                  </div>
                  <div style={{ fontSize: '0.8rem', opacity: 0.9, lineHeight: 1.55 }}>{step.sub}</div>
                </div>
                {i < arr.length - 1 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 0.35rem',
                      fontSize: '1.5rem',
                      color: '#aaa',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    →
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="xen-two-col" style={{ gap: '2.5rem' }}>
            <div>
              <h3 style={{ color: 'var(--xen-navy)', marginBottom: '0.75rem' }}>Schema normalization</h3>
              <p>
                Every PMS stores claim data differently. Dentrix uses its own table structure, field names, and
                relationship model. Open Dental uses another. CareStack's API returns yet another format. The
                DataVault normalization layer maps all of them to a single canonical dental data schema —
                one that models patients, insurance plans, procedures, claims, payments, and providers in
                a consistent way regardless of source system.
              </p>
              <p style={{ marginTop: '1rem' }}>
                This means that a claim from a Dentrix location and a claim from an Open Dental location
                look identical in the warehouse — same field names, same data types, same relationship
                structure. Analytics tools don't need to know which PMS the data came from. They just
                query clean, consistent dental data.
              </p>
            </div>
            <div>
              <h3 style={{ color: 'var(--xen-navy)', marginBottom: '0.75rem' }}>Incremental sync and history</h3>
              <p>
                DataVault doesn't just do a one-time migration — it maintains a continuous, incremental
                sync with each connected PMS. New claims, updated adjudication status, posted payments,
                and modified records flow into the warehouse as they occur. Sync intervals are configurable
                per system, from near-real-time for critical revenue cycle data to daily for historical
                reporting purposes.
              </p>
              <p style={{ marginTop: '1rem' }}>
                All historical data is preserved in the warehouse with complete sync metadata — timestamps,
                source system identifiers, and change records. This makes DataVault suitable not only for
                real-time analytics but also for historical trending, year-over-year comparison, and
                acquisition due diligence analysis going back as far as the source PMS data exists.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What you get from unified PMS data */}
      <section className="xen-section xen-section-center" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">Use Cases</p>
          <h2>What unified PMS data enables for your DSO</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Once your PMS data is unified in DataVault, a wide range of analytical and operational
            capabilities become available that simply aren't possible with fragmented source systems.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1.5rem',
            }}
          >
            {useCaseItems.map((uc) => (
              <div
                key={uc.title}
                style={{
                  background: '#f8f9ff',
                  borderRadius: '12px',
                  padding: '1.75rem',
                  borderLeft: '4px solid var(--xen-accent)',
                  textAlign: 'left',
                }}
              >
                <h3 style={{ color: 'var(--xen-navy)', fontSize: '1rem', marginBottom: '0.6rem' }}>{uc.title}</h3>
                <p style={{ color: '#555', fontSize: '0.9rem', lineHeight: 1.65, margin: 0 }}>{uc.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration badges */}
      <section className="xen-section xen-section-center xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">Supported Platforms</p>
          <h2>Every major U.S. dental PMS, under one roof.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2rem' }}>
            NDS DataVault maintains active connectors for all major dental PMS platforms —
            no integration project required on your end.
          </p>
          <div className="xen-integrations">
            {integrationItems.map((name) => (
              <div key={name} className="xen-integration-badge">
                {name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="xen-section xen-section-dark xen-section-center">
        <div className="xen-container">
          <h2>Unify your PMS data and unlock enterprise analytics.</h2>
          <p className="xen-section-subtitle" style={{ marginTop: '1rem', marginBottom: '2rem' }}>
            Tell us which PMS platforms are in your current environment and how many locations you're
            operating. We'll show you exactly how NDS DataVault would integrate them and what analytics
            become available once the data is unified.
          </p>
          <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.875rem 2.5rem', fontSize: '1.05rem' }}>
            Request a Demo
          </Link>
        </div>
      </section>
    </div>
  );
}
